import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CheckCircle2, Paperclip, Send } from 'lucide-react'
import { Modal } from '@symphenia/ui-components'
import {
  MAIL_EXPORT_LIMITS,
  type MailComposeDraft,
  type MailSourceType,
  type MailTransportChoice,
} from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { mailApi } from '@/lib/mail/mail.api'
import { exportMailen, type ExportRequest } from '@/lib/export-request'
import type { ExportFormat } from '@/lib/download'
import type { ExportOrientationChoice } from '@/components/ui/ExportButton'
import { EigeneDateien } from './EigeneDateien'

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'
const feldTitel = 'block text-xs font-semibold uppercase tracking-wide text-muted-foreground'

export interface MailExportDialogProps {
  open: boolean
  onClose: () => void
  /** Woher die Datei stammt — für „zuletzt"-Adressen und das Versandprotokoll. */
  source: MailSourceType
  /** Name des Exports, wie er im Anhang steht. */
  title: string
  /** Vorgabe-Betreff der Seite. */
  defaultSubject: string
  /** Die Anfrage für das gewählte Format — dieselbe wie beim Download. */
  request: (format: ExportFormat, orientation?: 'portrait' | 'landscape') => ExportRequest
  /** Wählbare Formate; ein einziges Format wird fest angezeigt. */
  formats: ExportFormat[]
  initialFormat?: ExportFormat
  /** Ausrichtung anbieten — nur, wo der Export-Knopf sie anbietet. */
  showOrientation?: boolean
  initialOrientation?: ExportOrientationChoice
  /** Hinweis über dem Anhang, z. B. „Monat nicht abgegeben". */
  notice?: string
}

const FORMAT_KEYS: Record<ExportFormat, string> = { xlsx: 'export.xlsx', docx: 'export.docx', pdf: 'export.pdf' }
const ORIENTATIONS: { value: ExportOrientationChoice; labelKey: string }[] = [
  { value: 'default', labelKey: 'export.orientationDefault' },
  { value: 'portrait', labelKey: 'export.orientationPortrait' },
  { value: 'landscape', labelKey: 'export.orientationLandscape' },
]

/**
 * AP-10 Nachtrag 5 — **Export per E-Mail.** Kein Beleg, kein Ansprechpartner:
 * Von, An (mit den zuletzt verwendeten Adressen), Betreff, Text mit Signatur und
 * der Export als Anhang. Die Datei erzeugt der Server mit denselben Parametern
 * wie beim Download. Dazu eigene Dateien vom Rechner (Nachtrag 5, 3a) — der
 * Server prüft Endung und Inhalt, hier wird nur vorsortiert.
 *
 * Vorbild für „Von" ist der Belegdialog (AP-10 Nachtrag 1, 13.3); welche Wege es
 * gibt, sagt der Server, und er prüft beim Senden erneut.
 */
export function MailExportDialog({
  open,
  onClose,
  source,
  title,
  defaultSubject,
  request,
  formats,
  initialFormat,
  showOrientation = false,
  initialOrientation = 'default',
  notice,
}: MailExportDialogProps) {
  const { t } = useTranslation()
  const [entwurf, setEntwurf] = useState<MailComposeDraft | null>(null)
  const [an, setAn] = useState('')
  const [kopie, setKopie] = useState(false)
  const [betreff, setBetreff] = useState('')
  const [nachricht, setNachricht] = useState('')
  const [weg, setWeg] = useState<MailTransportChoice | null>(null)
  const [format, setFormat] = useState<ExportFormat>('pdf')
  const [ausrichtung, setAusrichtung] = useState<ExportOrientationChoice>('default')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [gesendet, setGesendet] = useState<string[] | null>(null)
  const textfeld = useRef<HTMLTextAreaElement>(null)
  const [dateien, setDateien] = useState<File[]>([])
  const ladeEntwurf = (vollstaendig: boolean) =>
    mailApi
      .compose(source)
      .then(({ data }) => {
        setEntwurf(data.data)
        setWeg(data.data.defaultTransport)
        if (!vollstaendig) return
        // Oben die leere Zeile für den eigenen Text, darunter die Signatur (5.).
        setNachricht(data.data.signature ? `\n\n${data.data.signature}` : '')
        // Cursor an den Anfang — geschrieben wird über der Signatur.
        requestAnimationFrame(() => textfeld.current?.setSelectionRange(0, 0))
      })
      .catch((err) => setFehler(fehlertext(err, t)))

  useEffect(() => {
    if (!open) return
    setFehler(null)
    setEntwurf(null)
    setGesendet(null)
    setDateien([])
    setAn('')
    setKopie(false)
    setBetreff(defaultSubject)
    setFormat(initialFormat && formats.includes(initialFormat) ? initialFormat : formats.includes('pdf') ? 'pdf' : formats[0])
    setAusrichtung(initialOrientation)
    void ladeEntwurf(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source])

  const adressen = an
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean)

  const hinzufuegen = (email: string) => {
    if (adressen.some((a) => a.toLowerCase() === email.toLowerCase())) return
    setAn([...adressen, email].join(', '))
  }

  const ms = entwurf?.transports.microsoft365 ?? null
  const system = entwurf?.transports.system ?? null
  const ueberMicrosoft = weg === 'MICROSOFT_365'

  const senden = async () => {
    setFehler(null)
    setLaeuft(true)
    try {
      const { data } = await exportMailen(request(format, ausrichtung === 'default' ? undefined : ausrichtung), {
        to: adressen,
        copyToSelf: kopie && !ueberMicrosoft,
        subject: betreff.trim(),
        message: nachricht,
        ...(weg ? { transport: weg } : {}),
      }, dateien)
      setGesendet(data.data.recipients)
    } catch (err) {
      setFehler(fehlertext(err, t))
      // Abgelaufene Verbindung: Wege neu laden, damit die Auswahl stimmt — die Eingaben bleiben.
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
      if (code === 'MAIL_CONNECTION_EXPIRED') void ladeEntwurf(false)
    } finally {
      setLaeuft(false)
    }
  }

  const fertig = gesendet !== null

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="lg"
      title={t('mailExport.title')}
      footer={
        <div className="flex justify-end gap-2">
          {fertig ? (
            <button type="button" onClick={onClose} className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">
              {t('common.close')}
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={laeuft || !entwurf || !weg || adressen.length === 0 || !betreff.trim()}
                onClick={() => void senden()}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {laeuft ? t('mailExport.sending') : t('mailExport.submit')}
              </button>
            </>
          )}
        </div>
      }
    >
      {fertig ? (
        <div className="flex items-start gap-3 py-2" role="status">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
          <div>
            <p className="text-sm font-medium text-foreground">{t('mailExport.sent')}</p>
            <p className="text-sm text-muted-foreground">{gesendet.join(', ')}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {entwurf && !ueberMicrosoft && <p className="text-xs text-muted-foreground">{t('mailExport.replyTo', { email: entwurf.replyTo })}</p>}

          {/* Von — wie im Belegdialog */}
          {entwurf && (ms || !system) && (
            <div className="space-y-1">
              <span className={feldTitel}>{t('mailExport.from')}</span>
              {ms && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="mailExportTransport" disabled={ms.status !== 'OK'} checked={weg === 'MICROSOFT_365'} onChange={() => setWeg('MICROSOFT_365')} />
                  {t('mailExport.fromMicrosoft', { address: ms.address ?? '' })}
                  {ms.status === 'EXPIRED' && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{t('mailConnection.status.EXPIRED')}</span>
                  )}
                </label>
              )}
              {system && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="mailExportTransport" checked={weg === 'SYSTEM'} onChange={() => setWeg('SYSTEM')} />
                  {t('mailExport.fromSystem', { address: system.address })}
                </label>
              )}
              {(ms?.status === 'EXPIRED' || (!ms && !system)) && (
                <p className="text-xs text-muted-foreground">
                  {t(ms?.status === 'EXPIRED' ? 'mailExport.expiredHint' : 'mailExport.connectRequired')}{' '}
                  <Link to="/profile" className="text-primary hover:underline">
                    {t('mailExport.toProfile')}
                  </Link>
                </p>
              )}
            </div>
          )}
          {entwurf?.connectHint && !ms && system && (
            <p className="text-xs text-muted-foreground">
              {t('mailExport.connectHint')}{' '}
              <Link to="/profile" className="text-primary hover:underline">
                {t('mailExport.toProfile')}
              </Link>
            </p>
          )}

          <label className="block space-y-1">
            <span className={feldTitel}>{t('mailExport.to')}</span>
            <input className={eingabe} value={an} onChange={(e) => setAn(e.target.value)} placeholder="name@example.com" autoComplete="email" />
            <span className="block text-xs text-muted-foreground">{t('mailExport.toHint')}</span>
          </label>
          {entwurf && entwurf.recentRecipients.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">{t('mailExport.recent')}:</span>
              {entwurf.recentRecipients.map((email) => (
                <button key={email} type="button" onClick={() => hinzufuegen(email)} className="rounded-full border border-border px-2 py-0.5 hover:bg-muted">
                  {email}
                </button>
              ))}
            </div>
          )}
          {ueberMicrosoft ? (
            <p className="text-xs text-muted-foreground">{t('mailExport.sentItemsHint')}</p>
          ) : (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={kopie} onChange={(e) => setKopie(e.target.checked)} />
              {t('mailExport.copyToSelf')}
            </label>
          )}

          <label className="block space-y-1">
            <span className={feldTitel}>{t('mailExport.subject')}</span>
            <input className={eingabe} value={betreff} maxLength={MAIL_EXPORT_LIMITS.subject} onChange={(e) => setBetreff(e.target.value)} />
          </label>
          <label className="block space-y-1">
            <span className={feldTitel}>{t('mailExport.message')}</span>
            <textarea
              ref={textfeld}
              className={eingabe}
              rows={9}
              value={nachricht}
              maxLength={MAIL_EXPORT_LIMITS.message}
              onChange={(e) => setNachricht(e.target.value)}
            />
            <span className="block text-xs text-muted-foreground">
              {t('mailExport.signatureHint')}{' '}
              <Link to="/profile" className="text-primary hover:underline">
                {t('mailExport.signatureEdit')}
              </Link>
            </span>
          </label>

          <div className="space-y-2 rounded-md border border-border p-3">
            {notice && <p className="rounded-md bg-amber-100 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{notice}</p>}
            <p className="flex items-center gap-1.5 text-sm text-foreground">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              {t('mailExport.attachment')}: {title}
            </p>
            {formats.length > 1 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{t('mailExport.format')}</span>
                {formats.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFormat(f)}
                    aria-pressed={format === f}
                    className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                      format === f ? 'border-primary bg-primary/10 font-medium text-primary' : 'border-input text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t(FORMAT_KEYS[f])}
                  </button>
                ))}
              </div>
            )}
            {showOrientation && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{t('export.orientation')}</span>
                {ORIENTATIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setAusrichtung(o.value)}
                    aria-pressed={ausrichtung === o.value}
                    className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                      ausrichtung === o.value ? 'border-primary bg-primary/10 font-medium text-primary' : 'border-input text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t(o.labelKey)}
                  </button>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">{t('mailExport.attachmentHint')}</p>

            {/* Nachtrag 5, 3a: eigene Dateien vom Rechner */}
            <div className="border-t border-border pt-2">
              <EigeneDateien dateien={dateien} onChange={setDateien} ueberMicrosoft={ueberMicrosoft} />
            </div>
          </div>

          {fehler && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
        </div>
      )}
    </Modal>
  )
}
